import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Button, Container, Typography, Grid, Card, CardContent,
    Stack, useTheme, useMediaQuery, Avatar
} from '@mui/material';
import {
    FaLeaf, FaLightbulb, FaFan, FaTint, FaWind, FaThermometerHalf,
    FaMicrochip, FaPlay, FaArrowRight, FaRobot, FaUsers,
    FaSlidersH, FaShieldAlt, FaShoppingCart, FaPowerOff
} from 'react-icons/fa';
import { useApp } from '../../context/AppContext';

// Componente de revelação: aplica animação suave de fade/slide ao montar.
// Usa classe CSS global (index.css) com delay escalonado — funciona em qualquer ambiente.
const REVEAL_CLASS = {
    up: 'ag-reveal-up',
    down: 'ag-reveal-down',
    left: 'ag-reveal-left',
    right: 'ag-reveal-right'
};

function Reveal({ children, delay = 0, direction = 'up', sx = {} }) {
    const cls = REVEAL_CLASS[direction] || 'ag-reveal-up';
    return (
        <Box className={cls} sx={{ animationDelay: `${delay}ms`, ...sx }}>
            {children}
        </Box>
    );
}

// Efeito de escrita letra por letra (typewriter)
function Typewriter({ text, speed = 45, delay = 0, variant = 'h2', sx = {}, as: Component = Typography }) {
    const [shown, setShown] = React.useState('');
    const [started, setStarted] = React.useState(false);
    React.useEffect(() => {
        let timer;
        const startTimer = setTimeout(() => setStarted(true), delay);
        return () => clearTimeout(startTimer);
    }, [delay]);
    React.useEffect(() => {
        if (!started) return;
        let i = 0;
        const id = setInterval(() => {
            i++;
            setShown(text.slice(0, i));
            if (i >= text.length) clearInterval(id);
        }, speed);
        return () => clearInterval(id);
    }, [started, text, speed]);
    return (
        <Component variant={variant} sx={{ ...sx }}>
            {shown}
            {shown.length < text.length && (
                <Box component="span" sx={{ display: 'inline-block', width: '2px', ml: 0.5, animation: 'agBlink 1s step-end infinite', color: 'primary.main' }}>|</Box>
            )}
        </Component>
    );
}

const EQUIPMENTS = [
    { icon: FaLightbulb, title: 'Controle quantas luzes quiser', desc: 'Automatize várias luzes com horários e intensidade ideais para cada fase do cultivo.' },
    { icon: FaTint, title: 'Irrigação / Rega', desc: 'Bomba de rega em mL com agendamento por intervalo. Rega precisa, sem desperdício nem encharcamento.' },
    { icon: FaFan, title: 'Ventilação', desc: 'Ventiladores e exaustão para renovação do ar, controlando temperatura e umidade do ambiente.' },
    { icon: FaWind, title: 'Umidificador', desc: 'Mantém a umidade ideal do ar para a saúde das plantas em qualquer estação do ano.' },
    { icon: FaThermometerHalf, title: 'Sensores', desc: 'Acompanhe temperatura, umidade e VPD em tempo real.' },
    { icon: FaMicrochip, title: 'Processador com Wi-Fi', desc: 'Um processador com conexão Wi-Fi que integra todos os seus equipamentos.' }
];

const STEPS = [
    { n: 1, title: 'Conectar', desc: 'Ligue o equipamento e conecte todos os seus acessórios a ele.' },
    { n: 2, title: 'Configurar', desc: 'Instale o aplicativo oficial AutoGrow BR, vincule sua conta e conecte seu controlador.' },
    { n: 3, title: 'Automatizar', desc: 'Escolha seu modo de cultivo e comece seu plantio.' }
];

const BENEFITS = [
    { icon: FaRobot, title: 'Automação total', desc: 'Cultivo que se cuida sozinho, 24h por dia.' },
    { icon: FaUsers, title: 'Para todos', desc: 'Intuitivo e acessível, mesmo sem experiência.' },
    { icon: FaSlidersH, title: 'Controle simplificado', desc: 'Tudo em um só lugar, com poucos toques.' },
    { icon: FaShieldAlt, title: 'Confiável', desc: 'Receba avisos sobre rega, luz, temperatura e todo o seu cultivo.' }
];

// Imagens locais (public/landing) — cultivo indoor / grow com iluminação
// Evita bloqueio ORB de imagens externas e garante carregamento consistente.
const HERO_IMG = '/landing/led.jpg';
const EQUIP_IMG = {
    'Controle quantas luzes quiser': '/landing/led.jpg',
    'Irrigação / Rega': '/landing/g3.jpg',
    'Ventilação': '/landing/g4.jpg',
    'Umidificador': '/landing/g1.jpg',
    'Sensores': '/landing/g2.jpg',
    'Processador com Wi-Fi': '/landing/esp32.jpg'
};

function ImgBox({ src, sx }) {
    const [ok, setOk] = React.useState(true);
    return (
        <Box sx={{ ...sx, backgroundImage: ok ? `url(${src})` : 'linear-gradient(135deg, rgba(76,175,80,0.3), rgba(33,150,243,0.2))' }}>
            {ok && (
                <Box
                    component="img"
                    src={src}
                    alt=""
                    onError={() => setOk(false)}
                    sx={{ width: 1, height: 1, objectFit: 'cover', display: 'block' }}
                />
            )}
        </Box>
    );
}

function EquipmentCard({ icon: Icon, title, desc }) {
    const img = EQUIP_IMG[title];
    return (
        <Card sx={{ height: '100%', background: 'rgba(255,255,255,0.04)', backdropFilter: 'blur(6px)', border: '1px solid rgba(76,175,80,0.18)', overflow: 'hidden' }}>
            {img && (
                <Box sx={{ height: 140, position: 'relative' }}>
                    <ImgBox src={img} sx={{ position: 'absolute', inset: 0, backgroundSize: 'cover', backgroundPosition: 'center' }} />
                    <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(0,0,0,0.1), rgba(18,18,18,0.85))' }} />
                    <Avatar sx={{ position: 'absolute', bottom: 12, left: 16, bgcolor: 'primary.main', width: 48, height: 48, boxShadow: 3 }}>
                        <Icon size={22} color="#fff" />
                    </Avatar>
                </Box>
            )}
            <CardContent>
                <Typography variant="h6" gutterBottom>{title}</Typography>
                <Typography variant="body2" color="text.secondary">{desc}</Typography>
            </CardContent>
        </Card>
    );
}

export default function LandingPage() {
    const navigate = useNavigate();
    const { authed, devices } = useApp();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [loading, setLoading] = React.useState(true);
    const [scrollProgress, setScrollProgress] = React.useState(0);
    const hasConfiguredDevice = authed && devices.length > 0;

    // Animação de germinação: permanece por 4,5s antes de revelar a home
    useEffect(() => {
        const t = setTimeout(() => setLoading(false), 4500);
        return () => clearTimeout(t);
    }, []);

    // Barra de progresso de scroll (transição leve entre seções)
    useEffect(() => {
        const onScroll = () => {
            const h = document.documentElement;
            const max = h.scrollHeight - h.clientHeight;
            setScrollProgress(max > 0 ? (h.scrollTop / max) * 100 : 0);
        };
        window.addEventListener('scroll', onScroll, { passive: true });
        onScroll();
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    return (
        <Box sx={{ bgcolor: 'background.default', color: 'text.primary', minHeight: '100vh' }}>
            {/* Overlay de carregamento: semente germinando */}
            <Box className={loading ? 'ag-loader-show' : 'ag-loader-hide'} sx={{
                position: 'fixed', inset: 0, zIndex: 9999, display: 'flex',
                flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                background: '#0c120c',
                transition: 'opacity .6s ease, visibility .6s ease'
            }}>
                <Box className="ag-growth-scene" aria-label="Ligando o AutoGrow">
                    <Box className="ag-power-button">
                        <FaPowerOff size={26} />
                    </Box>
                    <Box className="ag-new-sun" />
                    <Box className="ag-ground-line" />
                    <Stack className="ag-brand-sprout" direction="row" spacing={0.8} alignItems="center">
                        <FaLeaf size={28} color="#72d65d" />
                        <Typography component="span">Auto<span>Grow</span> BR</Typography>
                    </Stack>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 2 }}>Cultivando sua automação...</Typography>
            </Box>
            {/* Header */}
            <Box component="header" sx={{ position: 'sticky', top: 0, zIndex: 10, backdropFilter: 'blur(10px)', background: 'rgba(18,18,18,0.7)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                <Box sx={{ height: 3, width: `${scrollProgress}%`, background: 'linear-gradient(90deg, #4CAF50, #2196F3)', transition: 'width .15s ease-out' }} />
                <Container maxWidth="lg" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5 }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ cursor: 'pointer' }} onClick={() => navigate('/')}>
                        <FaLeaf size={26} color="#4CAF50" />
                        <Typography variant="h6" sx={{ fontWeight: 700, letterSpacing: 0.5 }}>AutoGrow BR</Typography>
                    </Stack>
                    <Stack direction="row" spacing={1} sx={{ display: { xs: 'none', md: 'flex' } }}>
                        <Button color="inherit" onClick={() => navigate('/guia')}>Guia de Cultivo</Button>
                        <Button color="inherit" onClick={() => navigate('/comprar')}>Comprar</Button>
                        <Button variant="contained" endIcon={<FaArrowRight />} onClick={() => navigate('/login')}>
                            Entrar
                        </Button>
                    </Stack>
                    <Button variant="contained" endIcon={<FaArrowRight />} onClick={() => navigate('/login')} sx={{ display: { xs: 'inline-flex', md: 'none' } }}>
                        Entrar
                    </Button>
                </Container>
            </Box>

            {/* Hero */}
            <Box sx={{
                position: 'relative', overflow: 'hidden',
                background: 'radial-gradient(1200px 600px at 70% -10%, rgba(76,175,80,0.25), transparent), radial-gradient(900px 500px at 10% 10%, rgba(33,150,243,0.18), transparent)'
            }}>
                <ImgBox src={HERO_IMG} sx={{ position: 'absolute', inset: 0, backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', opacity: 0.25 }} />
                <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(18,18,18,0.82) 0%, rgba(18,18,18,0.7) 50%, rgba(18,18,18,0.92) 100%)' }} />
                <Container maxWidth="lg" sx={{ pt: { xs: 2, md: 3 }, pb: { xs: 8, md: 12 }, position: 'relative', zIndex: 1 }}>
                    <Grid container spacing={4} alignItems="center">
                        <Grid item xs={12} md={7}>
                            <Typewriter
                                text="Gerencie seu grow de forma simples e automática"
                                speed={38}
                                delay={950}
                                variant={isMobile ? 'h3' : 'h2'}
                                sx={{ fontWeight: 800, lineHeight: 1.1 }}
                            />
                            <Typography variant="h6" color="text.secondary" sx={{ mt: 2, fontWeight: 400 }}>
                                <Typewriter
                                    text="Tenha controle total do seu cultivo, de forma simples e automática."
                                    speed={18}
                                    delay={2600}
                                    variant="h6"
                                />
                            </Typography>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 4 }}>
                                <Button size="large" variant="contained" endIcon={<FaArrowRight />} onClick={() => navigate('/login')}>
                                    Conhecer o produto
                                </Button>
                                <Button size="large" variant="outlined" startIcon={<FaShoppingCart />} onClick={() => navigate('/comprar')}>
                                    AutoGrow BR Shop
                                </Button>
                            </Stack>
                            <Stack direction="row" spacing={4} sx={{ mt: 5 }}>
                                <Box><Typography variant="h4" sx={{ fontWeight: 800, color: 'primary.main' }}>6+</Typography><Typography variant="body2" color="text.secondary">Equipamentos</Typography></Box>
                                <Box><Typography variant="h4" sx={{ fontWeight: 800, color: 'primary.main' }}>100%</Typography><Typography variant="body2" color="text.secondary">Automatizável</Typography></Box>
                                <Box><Typography variant="h4" sx={{ fontWeight: 800, color: 'primary.main' }}>24/7</Typography><Typography variant="body2" color="text.secondary">Monitoramento</Typography></Box>
                            </Stack>
                        </Grid>
                        <Grid item xs={12} md={5}>
                            <Box sx={{
                                position: 'relative', borderRadius: 4, overflow: 'hidden', textAlign: 'center',
                                background: 'linear-gradient(160deg, rgba(76,175,80,0.18), rgba(33,150,243,0.12))',
                                border: '1px solid rgba(76,175,80,0.25)',
                                boxShadow: '0 20px 50px rgba(0,0,0,0.35)'
                            }}>
                                <Box
                                    component="img"
                                    src="/landing/hero-device.jpg"
                                    alt="AutoGrow BR controlando uma estufa pelo celular"
                                    sx={{ width: 1, display: 'block', aspectRatio: '3 / 2', objectFit: 'cover' }}
                                />
                                <Box sx={{ p: 2.5 }}>
                                    <Typography variant="h6" sx={{ fontWeight: 700 }}>Sua estufa, no seu bolso</Typography>
                                    <Typography variant="body2" color="text.secondary">Acompanhe e controle tudo de onde estiver.</Typography>
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* Imagem demonstrativa do equipamento */}
            <Box sx={{ py: { xs: 5, md: 8 }, background: 'rgba(255,255,255,0.02)' }}>
                <Container maxWidth="lg">
                    <Box sx={{
                        p: { xs: 2, md: 4 },
                        borderRadius: 4,
                        background: 'linear-gradient(135deg, rgba(76,175,80,0.14), rgba(33,150,243,0.08))',
                        border: '1px solid rgba(76,175,80,0.24)',
                        boxShadow: '0 20px 55px rgba(0,0,0,0.3)'
                    }}>
                        <Grid container spacing={{ xs: 3, md: 5 }} alignItems="center">
                            <Grid item xs={12} md={5}>
                                <Typography variant="overline" sx={{ color: 'primary.main', fontWeight: 800, letterSpacing: 1.5 }}>
                                    Hardware AutoGrow BR
                                </Typography>
                                <Typography variant="h4" sx={{ mt: 0.5, fontWeight: 800, lineHeight: 1.1 }}>
                                    O controlador do seu grow
                                </Typography>
                                <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
                                    O cérebro da automação: centraliza iluminação, ventilação, irrigação e sensores em um único equipamento.
                                </Typography>
                                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 3 }}>
                                    {['6 relés', 'Wi-Fi', 'Automação 24/7'].map((label) => (
                                        <Box key={label} sx={{ px: 1.5, py: 0.75, borderRadius: 10, background: 'rgba(76,175,80,0.16)', color: 'primary.light', fontSize: 13, fontWeight: 700 }}>
                                            {label}
                                        </Box>
                                    ))}
                                </Stack>
                            </Grid>
                            <Grid item xs={12} md={7}>
                                <Box sx={{ borderRadius: 3, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.16)', background: 'rgba(0,0,0,0.2)' }}>
                                    <Box
                                        component="img"
                                        src="/landing/demo-equipment.jpg"
                                        alt="Controlador AutoGrow BR"
                                        sx={{ width: 1, display: 'block', aspectRatio: '3 / 2', objectFit: 'cover' }}
                                    />
                                </Box>
                            </Grid>
                        </Grid>
                    </Box>
                </Container>
            </Box>

            {/* Equipment */}
            <Box sx={{ py: { xs: 6, md: 10 }, background: 'rgba(255,255,255,0.02)' }}>
                <Container maxWidth="lg">
                    <Reveal>
                        <Typography variant="h4" align="center" sx={{ fontWeight: 800, mb: 1 }}>Tudo conectado em uma central</Typography>
                        <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 5 }}>
                            Cada equipamento do seu grow integrado e gerenciado de forma simplificada.
                        </Typography>
                    </Reveal>
                    <Grid container spacing={3}>
                        {EQUIPMENTS.map((e, i) => (
                            <Grid item xs={12} sm={6} md={4} key={e.title}>
                            <Reveal delay={i * 100} sx={{ height: '100%' }}>
                                    <EquipmentCard {...e} />
                                </Reveal>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* How it works */}
            <Box sx={{ py: { xs: 6, md: 10 } }}>
                <Container maxWidth="lg">
                    <Reveal>
                        <Typography variant="h4" align="center" sx={{ fontWeight: 800, mb: 5 }}>Como funciona</Typography>
                    </Reveal>
                    <Grid container spacing={3}>
                        {STEPS.map((s, i) => (
                            <Grid item xs={12} md={4} key={s.n}>
                                <Reveal delay={i * 150} direction="up">
                                    <Box sx={{ textAlign: 'center', px: 2 }}>
                                        <Avatar sx={{ bgcolor: 'secondary.main', width: 64, height: 64, mx: 'auto', mb: 2, fontSize: 28, fontWeight: 800 }}>
                                            {s.n}
                                        </Avatar>
                                        <Typography variant="h6" gutterBottom>{s.title}</Typography>
                                        <Typography variant="body2" color="text.secondary">{s.desc}</Typography>
                                    </Box>
                                </Reveal>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* Benefits */}
            <Box sx={{ py: { xs: 6, md: 10 }, background: 'rgba(255,255,255,0.02)' }}>
                <Container maxWidth="lg">
                    <Reveal>
                        <Typography variant="h4" align="center" sx={{ fontWeight: 800, mb: 5 }}>Por que o AutoGrow BR?</Typography>
                    </Reveal>
                    <Grid container spacing={3}>
                        {BENEFITS.map((b, i) => (
                            <Grid item xs={12} sm={6} md={3} key={b.title}>
                                <Reveal delay={i * 100}>
                                    <Box sx={{ textAlign: 'center', px: 1 }}>
                                        <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56, mx: 'auto', mb: 2 }}>
                                            <b.icon size={24} color="#fff" />
                                        </Avatar>
                                        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 700 }}>{b.title}</Typography>
                                        <Typography variant="body2" color="text.secondary">{b.desc}</Typography>
                                    </Box>
                                </Reveal>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* Recursos: Guia de Cultivo + Compra */}
            <Box sx={{ py: { xs: 6, md: 10 }, background: 'rgba(255,255,255,0.02)' }}>
                <Container maxWidth="lg">
                    <Reveal>
                        <Typography variant="h4" align="center" sx={{ fontWeight: 800, mb: 1 }}>Explore mais do AutoGrow BR</Typography>
                        <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 5 }}>
                            Do conhecimento à ação: aprenda a cultivar qualquer planta e adquira o controlador.
                        </Typography>
                    </Reveal>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <Reveal direction="left">
                                <Card sx={{ height: '100%', background: 'linear-gradient(160deg, rgba(76,175,80,0.18), rgba(33,150,243,0.12))', border: '1px solid rgba(76,175,80,0.25)', borderRadius: 4 }}>
                                    <CardContent>
                                        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
                                            <Avatar sx={{ bgcolor: 'primary.main' }}><FaRobot color="#fff" /></Avatar>
                                            <Typography variant="h6" sx={{ fontWeight: 700 }}>Guia de Cultivo com IA</Typography>
                                        </Stack>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                            Um guia inteligente para cuidar de qualquer planta, com orientações simples sobre rega, luz, temperatura e colheita.
                                        </Typography>
                                        <Button variant="contained" startIcon={<FaRobot />} onClick={() => navigate('/guia')}>
                                            Abrir Guia de Cultivo
                                        </Button>
                                    </CardContent>
                                </Card>
                            </Reveal>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Reveal direction="right">
                                <Card sx={{ height: '100%', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 4 }}>
                                    <CardContent>
                                        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
                                            <Avatar sx={{ bgcolor: 'secondary.main' }}><FaShoppingCart color="#fff" /></Avatar>
                                            <Typography variant="h6" sx={{ fontWeight: 700 }}>Adquira o Controlador</Typography>
                                        </Stack>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                            Tenha o controle completo do seu cultivo em um único equipamento. Automatize sua rotina e cultive com mais praticidade.
                                        </Typography>
                                        <Button variant="outlined" startIcon={<FaShoppingCart />} onClick={() => navigate('/comprar')}>
                                            Ver Controlador
                                        </Button>
                                    </CardContent>
                                </Card>
                            </Reveal>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* Final CTA */}
            <Box sx={{
                py: { xs: 8, md: 12 },
                background: 'radial-gradient(800px 400px at 50% 0%, rgba(76,175,80,0.22), transparent)'
            }}>
                <Container maxWidth="md" sx={{ textAlign: 'center' }}>
                    <Typography variant={isMobile ? 'h4' : 'h3'} sx={{ fontWeight: 800 }}>
                        Pronto para cultivar com inteligência?
                    </Typography>
                    <Typography variant="h6" color="text.secondary" sx={{ mt: 2, fontWeight: 400 }}>
                        Acesse a central de comando e leve seu grow ao próximo nível.
                    </Typography>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center" sx={{ mt: 4 }}>
                        <Button size="large" variant="contained" endIcon={<FaArrowRight />} onClick={() => navigate(hasConfiguredDevice ? '/dashboard' : '/comprar')}>
                            {hasConfiguredDevice ? 'Entrar na Central' : 'Adquirir a controladora'}
                        </Button>
                        <Button size="large" variant="outlined" startIcon={<FaRobot />} onClick={() => navigate('/guia')}>
                            Guia de Cultivo
                        </Button>
                    </Stack>
                </Container>
            </Box>

            {/* Footer */}
            <Box component="footer" sx={{ py: 4, borderTop: '1px solid rgba(255,255,255,0.06)', textAlign: 'center' }}>
                <Container maxWidth="lg">
                    <Stack direction="row" spacing={1} justifyContent="center" alignItems="center" sx={{ mb: 1 }}>
                        <Box component="a" href="https://3dartprint.site/" target="_blank" rel="noreferrer" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                            <Box component="img" src="/logo-3d-art-print.png" alt="3D Art Print" sx={{ width: 42, height: 42, objectFit: 'contain' }} />
                        </Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>3D Art Print</Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                        © 2026 3D Art Print. Todos os direitos reservados.
                    </Typography>
                </Container>
            </Box>
        </Box>
    );
}
